import { useContext, useMemo, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { RadioGroupOptions, RadioGroupVariants, Switch, SearchInput } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext } from 'src/AnalyticsContext';
import { Unit } from 'src/store/sageAdminApi';
import { pluralize } from 'src/utils/intlUtils';

const DrawerName = 'Select Unit Drawer';
const SearchPlaceholder = 'Search by Unit name';

export interface SelectUnitDrawerProps extends DrawerComponentProps {
    units: (Unit & { value: string })[];
    preSelectedUnitId?: string;
    showButtonsFilter?: boolean;
}

function SelectUnitDrawer(props: SelectUnitDrawerProps) {
    const {
        units,
        preSelectedUnitId,
        onNext,
        title = 'Select Unit',
        showButtonsFilter = false,
    } = props;
    const { trackEvent, trackSearchInput } = useContext(AnalyticsContext);
    const searchInputAnalytics = trackSearchInput(DrawerName, SearchPlaceholder);
    const [selectedUnit, setSelectedUnit] = useState(preSelectedUnitId || '');
    const [searchTerm, setSearchTerm] = useState('');
    const [showUnitsWithNoDevices, setShowUnitsWithNoDevices] = useState(false);

    const filteredUnits = useMemo(() => {
        const unitsByDeviceCount = showUnitsWithNoDevices
            ? units?.filter((unit) => !unit.devices)
            : units;

        if (!!searchTerm) {
            return unitsByDeviceCount?.filter((unit) =>
                unit.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        } else {
            return unitsByDeviceCount;
        }
    }, [units, searchTerm, showUnitsWithNoDevices]);

    const handleUnitsFilter = () => {
        trackEvent('Toggle Clicked', {
            context: DrawerName,
            action: 'Filter units with no devices',
            value: showUnitsWithNoDevices,
        });
        setShowUnitsWithNoDevices(!showUnitsWithNoDevices);
    };

    const handleSelectUnit = (unitId: string) => {
        trackEvent('Unit Selected', { unit: unitId, context: DrawerName });
        setSelectedUnit(unitId);
    };

    const handleContinue = () => {
        trackEvent('Continue Button Clicked', { context: DrawerName, unit: selectedUnit });
        onNext({ value: selectedUnit });
    };

    return (
        <>
            {/* Fixed Header */}
            <DrawerTitle title={title}>
                <SearchInput
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    placeholder={SearchPlaceholder}
                    {...searchInputAnalytics}
                />

                {showButtonsFilter && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'grey.900',
                            }}
                        >
                            Only show units with no devices assigned
                        </Typography>
                        <Switch
                            checked={showUnitsWithNoDevices}
                            value={showUnitsWithNoDevices}
                            onChange={handleUnitsFilter}
                            slotProps={{
                                input: {
                                    'aria-label': 'toggle units with no devices',
                                    name: 'toggle units with no devices',
                                },
                            }}
                        />
                    </Box>
                )}
            </DrawerTitle>
            {/* Scrolling Area */}
            <DrawerBody sx={{ py: 2.5 }}>
                {filteredUnits?.length > 0 && (
                    <RadioGroupOptions
                        radioGroupProps={{ 'aria-label': 'unit' }}
                        variant={RadioGroupVariants.Outlined}
                        selectedOption={selectedUnit}
                        onSelectOption={handleSelectUnit}
                        options={filteredUnits?.map((unit) => ({
                            value: unit.value,
                            label: (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                        pr: 2,
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'grey.900',
                                        }}
                                    >
                                        {unit.displayName}
                                    </Typography>
                                    {showButtonsFilter && unit.devices?.length && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'grey.900',
                                            }}
                                        >
                                            {pluralize(unit.devices.length, 'device', 'devices')}
                                        </Typography>
                                    )}
                                </Box>
                            ),
                        }))}
                    />
                )}

                {filteredUnits?.length === 0 && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'grey.700',
                        }}
                    >
                        {!!searchTerm ? 'No units found' : 'No units'}
                    </Typography>
                )}
            </DrawerBody>
            {/* Bottom Sticky Buttons */}
            <DrawerFooter sx={{ bgcolor: 'common.white' }}>
                <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={!selectedUnit}
                    onClick={handleContinue}
                >
                    Continue
                </Button>
            </DrawerFooter>
        </>
    );
}

export default SelectUnitDrawer;
